import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { pdfToHtml } from "../../lib/pdfToHtml";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import RichTextEditor, { editorOverflowsA4, OVERFLOW_MSG } from "../../components/RichTextEditor";
import TemplateAIButton from "../../components/TemplateAIButton";
import ResizableVarsPanel from "../../components/ResizableVarsPanel";
import PdfPreviewModal from "../../components/PdfPreviewModal";
import { LetterA4Preview } from "../../components/LetterA4Preview";
import { downloadPdf } from "../../lib/letterPdf";
import { FileText, Plus, Loader2, Folder, FolderOpen, FolderPlus, Trash2, Pencil, Share2, Lock, Save, FileUp, Calendar, Copy, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight, Eye, Star } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const TIPI_SINISTRO = ["RD", "RCA", "TRASPORTATO", "PEDONE", "CICLISTA", "ANIMALE", "ANOMALIA STRADA", "ALTRO"];
const FOLDER_COLORS = ["#ef4444", "#f97316", "#f59e0b", "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6", "#6366f1", "#a855f7", "#ec4899", "#64748b"];


pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs";

export default function Lettere() {
  const { user } = useAuth();
  const isOwner = user.role === "titolare";
  const [folders, setFolders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState("all");

  const [newFolder, setNewFolder] = useState("");
  const [tplOpen, setTplOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tpl, setTpl] = useState({ title: "", content: "", folder_id: "none", shared: false });
  const [previewOn, setPreviewOn] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [tplMargins, setTplMargins] = useState({ top: 20, right: 15, bottom: 15, left: 20 });
  const [tplFooter, setTplFooter] = useState(true);
  const [saving, setSaving] = useState(false);
  const [varCategories, setVarCategories] = useState([]);
  const [varOpen, setVarOpen] = useState({});
  const tplEditorRef = useRef(null);
  const toggleCat = (t) => setVarOpen((s) => ({ ...s, [t]: !s[t] }));
  const insertVar = (key, fmt) => {
    const token = "{{" + key + (fmt ? ":" + fmt : "") + "}}";
    tplEditorRef.current?.chain().focus().insertContent(token).run();
  };
  const varRow = (v) => (
    <div key={v.key} className="flex items-start justify-between gap-1" data-testid={`tpl-var-row-${v.key}`}>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-foreground font-medium truncate" title={v.label}>{v.label}</div>
        {v.value ? <div className="text-[12px] text-amber-600 font-mono truncate" title={v.value}>{v.value}</div> : null}
      </div>
      <div className="flex gap-0.5 shrink-0 pt-0.5">
        <button type="button" onClick={() => insertVar(v.key, "upper")} data-testid={`tpl-var-${v.key}-upper`} title="TUTTO MAIUSCOLO" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">AA</button>
        <button type="button" onClick={() => insertVar(v.key, "title")} data-testid={`tpl-var-${v.key}-title`} title="Prima lettera maiuscola" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">Aa</button>
        <button type="button" onClick={() => insertVar(v.key, "lower")} data-testid={`tpl-var-${v.key}-lower`} title="tutto minuscolo" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">aa</button>
        <button type="button" onClick={() => insertVar(v.key, "titlecase")} data-testid={`tpl-var-${v.key}-titlecase`} title="Nome e Cognome (Title Case)" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">Nc</button>
      </div>
    </div>
  );
  useEffect(() => {
    api.get("/studio/letter-variables").then(({ data }) => setVarCategories(data.categories || [])).catch(() => {});
    getStudio().then((s) => { if (s?.settings?.print_margins) setTplMargins(globalTplMargins()); }).catch(() => {});
  }, []);

  const [folderEdit, setFolderEdit] = useState(null); // { id, name, tipo_mode, tipi }
  const [savingFolder, setSavingFolder] = useState(false);

  // Anteprima PDF reale (dati di esempio) di un template
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfMeta, setPdfMeta] = useState({ title: "", filename: "template.pdf" });
  const [previewingId, setPreviewingId] = useState(null);
  const studioRef = useRef(null);

  const getStudio = async () => {
    if (studioRef.current) return studioRef.current;
    const { data } = await api.get("/studio/me");
    studioRef.current = data;
    const pm = data?.settings?.print_margins;
    if (pm && typeof pm.top === "number") setTplMargins({ top: pm.top, right: pm.right, bottom: pm.bottom, left: pm.left });
    setTplFooter(data?.settings?.pdf_footer !== false);
    return data;
  };
  useEffect(() => { getStudio().catch(() => {}); }, []);

  const previewTemplate = async (t) => {
    setPreviewingId(t.id);
    const tid = toast.loading("Generazione anteprima…");
    try {
      const studio = await getStudio();
      const { data } = await api.post("/studio/letter-templates/preview-sample", { content: t.content || "" });
      const sampleHtml = data.content || t.content || "";
      // Margini dalle Impostazioni Studio (stessi usati dall'editor); fallback ai default editor.
      const pm = studio?.settings?.print_margins;
      const margins = (pm && typeof pm.top === "number")
        ? { top: pm.top, right: pm.right, bottom: pm.bottom, left: pm.left }
        : { top: 20, right: 15, bottom: 15, left: 20 };
      // Nessuna intestazione/firma: l'editor del template mostra solo il contenuto,
      // quindi l'anteprima A4 (stesso motore PDF) resta fedele all'editor.
      const doc = await downloadPdf(`${t.title || "template"}.pdf`, sampleHtml, false, null, margins, { returnDoc: true, footer: studio?.settings?.pdf_footer !== false });
      const clean = (t.title || "template").replace(/[^\p{L}\p{N}\-_]+/gu, "_");
      setPdfMeta({ title: `Anteprima · ${t.title}`, filename: `${clean}.pdf` });
      setPdfDoc(doc);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Anteprima non riuscita");
    } finally {
      toast.dismiss(tid);
      setPreviewingId(null);
    }
  };

  const fileRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importDesc, setImportDesc] = useState("");
  const [importFolder, setImportFolder] = useState("");
  const [importText, setImportText] = useState("");
  const [importFile, setImportFile] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [importing, setImporting] = useState(false);

  const onPickPdf = async (file) => {
    if (file.type && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seleziona un file PDF"); return;
    }
    setImportFile(file.name);
    setImportDesc(file.name.replace(/\.pdf$/i, ""));
    setImportFolder(folders[0]?.id || "");
    setImportText("");
    setImportOpen(true);
    setExtracting(true);
    try {
      const aiPage = async (image, n, tot) => {
        toast.info(`Riconoscimento con intelligenza artificiale: pagina ${n} di ${tot}…`);
        const { data: job } = await api.post("/studio/letter-import/ai", { image });
        for (let t = 0; t < 120; t++) {
          await new Promise((r) => setTimeout(r, 2500));
          const { data } = await api.get(`/studio/letter-import/ai/${job.job_id}`);
          if (data.status === "done") return data.html || "";
          if (data.status === "error") throw new Error(data.error || "Errore AI");
        }
        throw new Error("Tempo scaduto");
      };
      const txt = await pdfToHtml(pdfjsLib, file, aiPage);
      if (!txt) toast.error("Nessun testo estraibile: il PDF sembra non testuale (scansione/immagine).");
      setImportText(txt);
    } catch (err) {
      toast.error(/^(Errore AI|Tempo scaduto)/.test(err?.message || "") ? `Riconoscimento non riuscito: ${err.message}` : "Impossibile leggere il PDF. Usa un PDF testuale generato da computer.");
      setImportText("");
    } finally { setExtracting(false); }
  };

  const doImport = async () => {
    if (!importDesc.trim()) { toast.error("Inserisci una descrizione / titolo"); return; }
    if (!importFolder) { toast.error("Seleziona una cartella"); return; }
    if (extracting) { toast.error("Attendere l'estrazione del testo"); return; }
    if (!importText.trim()) { toast.error("Nessun testo estratto dal PDF"); return; }
    setImporting(true);
    try {
      const payload = { title: importDesc.trim(), content: importText, shared: false, folder_id: importFolder };
      const { data } = await api.post("/studio/letter-templates", payload);
      toast.success("PDF importato come lettera");
      setImportOpen(false);
      await load();
      openEditTpl(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const openFolderEdit = (f) => setFolderEdit({ id: f.id, name: f.name, tipo_mode: f.tipo_mode || "tutte", tipi: f.tipi || [], colore: f.colore || "" });
  const toggleFav = async (f) => {
    try {
      await api.patch(`/studio/letter-folders/${f.id}`, { preferita: !f.preferita });
      setFolders((fs) => fs.map((x) => (x.id === f.id ? { ...x, preferita: !f.preferita } : x)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleTipo = (t) => setFolderEdit((fe) => ({ ...fe, tipi: fe.tipi.includes(t) ? fe.tipi.filter((x) => x !== t) : [...fe.tipi, t] }));
  const saveFolder = async () => {
    if (!folderEdit.name.trim()) { toast.error("Inserisci il nome cartella"); return; }
    setSavingFolder(true);
    try {
      await api.patch(`/studio/letter-folders/${folderEdit.id}`, {
        name: folderEdit.name.trim(),
        tipo_mode: folderEdit.tipo_mode,
        tipi: folderEdit.tipo_mode === "specifiche" ? folderEdit.tipi : [],
        colore: folderEdit.colore || "",
      });
      toast.success("Cartella aggiornata");
      setFolderEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingFolder(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/studio/letter-templates")];
      if (isOwner) reqs.push(api.get("/studio/letter-folders"));
      const res = await Promise.all(reqs);
      setTemplates(res[0].data);
      if (isOwner) setFolders(res[1].data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addFolder = async () => {
    if (!newFolder.trim()) return;
    try {
      await api.post("/studio/letter-folders", { name: newFolder.trim() });
      setNewFolder("");
      toast.success("Cartella creata");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delFolder = async (f) => {
    if (!window.confirm(`Eliminare la cartella "${f.name}"? I template resteranno senza cartella.`)) return;
    try { await api.delete(`/studio/letter-folders/${f.id}`); toast.success("Cartella eliminata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!tplOpen || !previewOn) return;
    const t = setTimeout(() => {
      api.post("/studio/letter-templates/preview-sample", { content: tpl.content || "" })
        .then(({ data }) => setPreviewHtml(data.content || "")).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [tplOpen, previewOn, tpl.content]);

  const globalTplMargins = () => {
    const pm = studioRef.current?.settings?.print_margins;
    return (pm && typeof pm.top === "number") ? { top: pm.top, right: pm.right, bottom: pm.bottom, left: pm.left } : { top: 20, right: 15, bottom: 15, left: 20 };
  };
  const openNewTpl = () => { setEditing(null); setTpl({ title: "", content: "", folder_id: "none", shared: false }); setTplMargins(globalTplMargins()); setPreviewOn(false); setTplOpen(true); };
  const openEditTpl = (t) => { setEditing(t); setTpl({ title: t.title, content: t.content || "", folder_id: t.folder_id || "none", shared: !!t.shared }); setTplMargins(t.margins && typeof t.margins.top === "number" ? t.margins : globalTplMargins()); setTplOpen(true); };

  const saveTpl = async (e) => {
    e.preventDefault();
    if (editorOverflowsA4()) toast.warning(OVERFLOW_MSG);
    setSaving(true);
    const payload = { title: tpl.title, content: tpl.content, shared: tpl.shared, folder_id: tpl.folder_id === "none" ? null : tpl.folder_id, margins: tplMargins };
    try {
      if (editing) await api.patch(`/studio/letter-templates/${editing.id}`, payload);
      else await api.post("/studio/letter-templates", payload);
      toast.success(editing ? "Template aggiornato" : "Template creato");
      setTplOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delTpl = async (t) => {
    if (!window.confirm(`Eliminare il template "${t.title}"?`)) return;
    try { await api.delete(`/studio/letter-templates/${t.id}`); toast.success("Template eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicateTpl = async (t) => {
    try {
      await api.post("/studio/letter-templates", { title: `${t.title} (copia)`, content: t.content || "", shared: !!t.shared, folder_id: t.folder_id || null });
      toast.success("Template duplicato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const folderName = (id) => folders.find((f) => f.id === id)?.name;
  const folderCount = (id) => templates.filter((t) => (t.folder_id || "none") === id).length;
  const [sort, setSort] = useState({ key: "title", dir: "asc" });
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortIcon = (k) => sort.key !== k
    ? <ArrowUpDown className="h-3 w-3 opacity-40" />
    : sort.dir === "asc" ? <ArrowUp className="h-3 w-3 text-amber-400" /> : <ArrowDown className="h-3 w-3 text-amber-400" />;
  const visible = templates
    .filter((t) => activeFolder === "all" || (t.folder_id || "none") === activeFolder)
    .slice()
    .sort((a, b) => {
      let av, bv;
      if (sort.key === "date") { av = a.updated_at || a.created_at || ""; bv = b.updated_at || b.created_at || ""; }
      else { av = (a.title || "").toLowerCase(); bv = (b.title || "").toLowerCase(); }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });

  return (
    <div data-testid="lettere-page">
      <PageHeader
        title="Gestione Lettere"
        subtitle="Cartelle, template con editor di testo ricco, variabili segnaposto e stampa con/senza carta intestata."
        icon={FileText}
        actions={
          <div className="flex gap-2">
            {isOwner && (
              <>
                <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="import-pdf-input"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickPdf(f); e.target.value = ""; }} />
                <Button onClick={() => fileRef.current?.click()} data-testid="import-pdf-button" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
                  <FileUp className="h-4 w-4 mr-1" /> Importa PDF
                </Button>
                <Button onClick={openNewTpl} data-testid="new-template-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  <Plus className="h-4 w-4 mr-1" /> Nuovo Template
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="flex gap-6 items-start">
        {/* Folders */}
        <ResizableVarsPanel storageKey="pgp_lettere_folders_w" testId="lettere-folders" defaultWidth={320} min={240} max={560} className="pgp-card p-4 h-fit pgp-fade-up">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-3">Cartelle</div>
          <button onClick={() => setActiveFolder("all")} data-testid="folder-all"
            className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-[10px] font-medium tracking-wide uppercase mb-1 ${activeFolder === "all" ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "text-slate-300 hover:bg-slate-800/60 border border-transparent"}`}>
            <Folder className="h-4 w-4" /> Tutti i template
            <span className="ml-auto shrink-0 min-w-[1.25rem] text-center rounded-full bg-slate-700 text-slate-100 text-[10px] font-mono px-1.5 py-0.5" data-testid="folder-count-all">{templates.length}</span>
          </button>
          {isOwner && [...folders.filter((f) => f.preferita).map((f) => ({ f, sec: "fav" })), ...folders.filter((f) => !f.preferita).map((f) => ({ f, sec: "other" }))].map(({ f, sec }, idx, arr) => (
            <div key={f.id}>
            {sec === "fav" && idx === 0 && <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-600 font-mono mt-2 mb-1 px-1" data-testid="folders-section-preferite"><Star className="h-3 w-3 fill-amber-400 text-amber-500" /> Preferite</div>}
            {sec === "other" && arr[0].sec === "fav" && arr[idx - 1]?.sec === "fav" && <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-3 mb-1 px-1 pt-2 border-t border-slate-700" data-testid="folders-section-altre">Altre cartelle</div>}
            <div className="flex items-center mb-0.5">
              <button onClick={() => toggleFav(f)} data-testid={`fav-folder-${f.id}`} title={f.preferita ? "Rimuovi dalle preferite" : "Segna come preferita"} className="shrink-0 px-1 transition-transform hover:scale-110">
                <Star className={`h-3.5 w-3.5 ${f.preferita ? "fill-amber-400 text-amber-500" : "text-slate-400 hover:text-amber-500"}`} />
              </button>
              <button onClick={() => setActiveFolder(f.id)} data-testid={`folder-${f.id}`}
                style={f.colore ? { backgroundColor: `${f.colore}2e`, borderLeft: `4px solid ${f.colore}` } : undefined}
                className={`flex-1 min-w-0 flex items-center gap-2 rounded-lg px-3 py-2 text-[10px] font-medium tracking-wide uppercase ${activeFolder === f.id ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "text-slate-300 hover:bg-slate-800/60 border border-transparent"}`}>
                <Folder className="h-4 w-4 shrink-0" style={f.colore ? { color: f.colore } : undefined} /> <span className="truncate min-w-0" title={f.name}>{f.name}</span>
                <span className="ml-auto shrink-0 min-w-[1.25rem] text-center rounded-full bg-slate-700 text-slate-100 text-[10px] font-mono px-1.5 py-0.5" data-testid={`folder-count-${f.id}`}>{folderCount(f.id)}</span>
              </button>
              <button onClick={() => openFolderEdit(f)} data-testid={`edit-folder-${f.id}`} title="Impostazioni cartella (tipologia sinistro)" className="shrink-0 text-slate-400 hover:text-cyan-600 px-1">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => delFolder(f)} data-testid={`del-folder-${f.id}`} title="Elimina cartella" className="shrink-0 text-slate-400 hover:text-red-500 px-1">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            </div>
          ))}
          {isOwner && (
            <div className="mt-3 flex gap-1.5">
              <Input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="Nuova cartella" data-testid="new-folder-input"
                className="h-9 bg-slate-900/60 border-slate-700 text-sm" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFolder())} />
              <Button onClick={addFolder} size="sm" data-testid="add-folder-button" className="h-9 bg-slate-700 hover:bg-slate-600"><FolderPlus className="h-4 w-4" /></Button>
            </div>
          )}
        </ResizableVarsPanel>

        {/* Templates */}
        <div className="pgp-fade-up flex-1 min-w-0">
          {loading ? (
            <div className="pgp-card p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : visible.length === 0 ? (
            <div className="pgp-card p-10 text-center text-slate-400 text-sm">
              {isOwner ? "Nessun template. Crea il primo template di lettera." : "Nessun template condiviso disponibile."}
            </div>
          ) : (
            <div className="pgp-card divide-y divide-slate-800/70 overflow-hidden" data-testid="lettere-list">
              <div className="flex items-center gap-4 px-4 py-2 bg-slate-900/40 text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                <span className="mr-1">Ordina:</span>
                <button type="button" onClick={() => toggleSort("title")} data-testid="sort-title" className="flex items-center gap-1 hover:text-slate-300 transition-colors">Titolo {sortIcon("title")}</button>
                <button type="button" onClick={() => toggleSort("date")} data-testid="sort-date" className="flex items-center gap-1 hover:text-slate-300 transition-colors">Data {sortIcon("date")}</button>
                <span className="ml-auto">Azioni</span>
              </div>
              {visible.map((t) => (
                <div key={t.id} data-testid={`template-row-${t.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-800/40 transition-colors">
                  <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-100 truncate">{t.title}</span>
                      {t.shared ? (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shrink-0 text-[10px] px-1.5 py-0"><Share2 className="h-2.5 w-2.5 mr-1" />Condiviso</Badge>
                      ) : (
                        <Badge className="bg-slate-500/15 text-slate-400 border border-slate-500/30 shrink-0 text-[10px] px-1.5 py-0"><Lock className="h-2.5 w-2.5 mr-1" />Privato</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono mt-0.5">
                      {t.folder_id && folderName(t.folder_id) && (
                        <span className="flex items-center gap-1 truncate"><Folder className="h-3 w-3" />{folderName(t.folder_id)}</span>
                      )}
                      <span className="flex items-center gap-1 shrink-0"><Calendar className="h-3 w-3" />{fmtDate(t.updated_at || t.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => previewTemplate(t)} disabled={previewingId === t.id} data-testid={`preview-tpl-${t.id}`} title="Anteprima con dati esempio" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-8 w-8 p-0">
                      {previewingId === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    {isOwner && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEditTpl(t)} data-testid={`edit-tpl-${t.id}`} title="Modifica" className="text-slate-400 hover:text-slate-100 hover:bg-slate-700/40 h-8 w-8 p-0"><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => duplicateTpl(t)} data-testid={`dup-tpl-${t.id}`} title="Duplica" className="text-slate-400 hover:text-slate-100 hover:bg-slate-700/40 h-8 w-8 p-0"><Copy className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => delTpl(t)} data-testid={`del-tpl-${t.id}`} title="Elimina" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"><Trash2 className="h-4 w-4" /></Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Template editor dialog */}
      {isOwner && (
        <Dialog open={tplOpen} onOpenChange={setTplOpen}>
          <DialogContent className="pgp-modal-full bg-[#121824] border-slate-700 text-slate-100 w-[calc(100vw-2rem)] max-w-none max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head">{editing ? "Modifica template" : "Nuovo template"}</DialogTitle></DialogHeader>
            <form onSubmit={saveTpl} className="space-y-4 min-w-0" data-testid="template-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Titolo *</Label>
                  <Input required value={tpl.title} onChange={(e) => setTpl({ ...tpl, title: e.target.value })} data-testid="tpl-title" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Cartella</Label>
                  <Select value={tpl.folder_id} onValueChange={(v) => setTpl({ ...tpl, folder_id: v })}>
                    <SelectTrigger data-testid="tpl-folder" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                      <SelectItem value="none">Nessuna cartella</SelectItem>
                      {folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-4">
                <ResizableVarsPanel storageKey="pgp_tpl_vars_w" testId="tpl-vars-panel" className="max-h-[56vh] overflow-y-auto pgp-card p-3 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono font-bold mb-1">Variabili</div>
                  <p className="text-[10px] text-slate-500 mb-2 leading-snug">Apri una categoria e clicca un formato per inserire la variabile nel punto del cursore.</p>
                  {varCategories.length === 0 ? (
                    <div className="text-[11px] text-slate-500">Nessuna variabile disponibile.</div>
                  ) : varCategories.map((cat) => {
                    const open = !!varOpen[cat.title];
                    return (
                      <div key={cat.title} className="rounded-lg border border-border bg-card overflow-hidden shadow-sm">
                        <button type="button" onClick={() => toggleCat(cat.title)} data-testid={`tpl-cat-${cat.title}`}
                          className="w-full flex items-center gap-2 px-3 py-2.5 text-left bg-muted hover:bg-accent transition-colors">
                          {open ? <FolderOpen className="h-4 w-4 text-amber-400 shrink-0" /> : <Folder className="h-4 w-4 text-amber-400/70 shrink-0" />}
                          <span className="text-[13px] font-semibold text-foreground flex-1 truncate">{cat.title}</span>
                          <span className="text-[9px] text-slate-500">{cat.subcats ? cat.subcats.length : cat.vars.length}</span>
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
                        </button>
                        {open && (
                          <div className="px-2 py-2 space-y-1.5 border-t border-border bg-popover">
                            {cat.subcats ? cat.subcats.map((sub) => {
                              const sk = cat.title + "/" + sub.title;
                              const sopen = !!varOpen[sk];
                              return (
                                <div key={sk} className="rounded-md border border-border/60 overflow-hidden">
                                  <button type="button" onClick={() => toggleCat(sk)} data-testid={`tpl-subcat-${sub.title}`}
                                    className="w-full flex items-center gap-2 px-2.5 py-2 text-left bg-muted/60 hover:bg-accent transition-colors">
                                    {sopen ? <FolderOpen className="h-3.5 w-3.5 text-amber-400 shrink-0" /> : <Folder className="h-3.5 w-3.5 text-amber-400/70 shrink-0" />}
                                    <span className="text-[12px] font-semibold text-foreground flex-1 truncate">{sub.title}</span>
                                    <span className="text-[9px] text-slate-500">{sub.vars.length}</span>
                                    {sopen ? <ChevronDown className="h-3 w-3 text-slate-500 shrink-0" /> : <ChevronRight className="h-3 w-3 text-slate-500 shrink-0" />}
                                  </button>
                                  {sopen && (
                                    <div className="px-2 py-1.5 space-y-1.5 border-t border-border bg-popover">
                                      {sub.vars.map((v) => varRow(v))}
                                    </div>
                                  )}
                                </div>
                              );
                            }) : cat.vars.map((v) => varRow(v))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </ResizableVarsPanel>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="text-slate-300 block">Contenuto</Label>
                    <div className="flex items-center gap-3">
                    {!previewOn && <TemplateAIButton editorRef={tplEditorRef} titolo={tpl.title} testId="tpl-ai" />}
                    <button type="button" onClick={() => setPreviewOn((v) => !v)} data-testid="tpl-preview-toggle"
                      className="text-xs font-semibold text-amber-400 hover:text-amber-300">{previewOn ? "◀ Torna all'editor" : "Anteprima con dati esempio ▶"}</button>
                    </div>
                  </div>
                  {previewOn ? (
                    <div className="rounded-md border border-slate-700 bg-slate-300/70 p-3 min-h-[320px] max-h-[440px] overflow-auto shadow-inner" data-testid="tpl-preview-panel">
                      <LetterA4Preview contentHtml={previewHtml || ""} withHead={false} inte={null} margins={tplMargins} footer={tplFooter} />
                    </div>
                  ) : (
                    <RichTextEditor value={tpl.content} onChange={(html) => setTpl((p) => ({ ...p, content: html }))} onEditorReady={(ed) => { tplEditorRef.current = ed; }} onMarginsChange={setTplMargins} initialMargins={tplMargins} />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3">
                <div>
                  <div className="text-sm text-slate-200">Condividi con gli operatori</div>
                  <div className="text-xs text-slate-500">Se disattivato, il template resta privato del titolare.</div>
                </div>
                <Switch checked={tpl.shared} onCheckedChange={(v) => setTpl({ ...tpl, shared: v })} data-testid="tpl-shared" className="data-[state=checked]:bg-amber-500" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="submit-template" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Salva template" : "Crea template"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}


      {pdfDoc && (
        <PdfPreviewModal doc={pdfDoc} title={pdfMeta.title} filename={pdfMeta.filename} onClose={() => setPdfDoc(null)} />
      )}

      {isOwner && (
        <Dialog open={importOpen} onOpenChange={(o) => { if (!o && !importing) setImportOpen(false); }}>
          <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="import-pdf-dialog">
            <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><FileUp className="h-5 w-5 text-amber-400" /> Importa PDF come lettera</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="text-xs text-slate-400 flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2">
                <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                <span className="truncate" data-testid="import-filename">{importFile || "—"}</span>
                {extracting && <span className="ml-auto flex items-center gap-1 text-amber-400 shrink-0"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Estrazione…</span>}
                {!extracting && importText && <span className="ml-auto text-emerald-400 shrink-0">{importText.replace(/<[^>]+>/g, "").length} caratteri</span>}
              </div>
              <div>
                <Label className="text-slate-300">Descrizione / Titolo lettera *</Label>
                <Input value={importDesc} onChange={(e) => setImportDesc(e.target.value)} data-testid="import-desc"
                  className="mt-1.5 bg-slate-900/60 border-slate-700" placeholder="Es. Richiesta risarcimento danni" />
              </div>
              <div>
                <Label className="text-slate-300">Cartella *</Label>
                <Select value={importFolder} onValueChange={setImportFolder}>
                  <SelectTrigger data-testid="import-folder" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Seleziona cartella" /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                    {folders.length === 0
                      ? <div className="px-3 py-2 text-sm text-slate-500">Nessuna cartella: creane una prima</div>
                      : folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setImportOpen(false)} disabled={importing} data-testid="import-cancel" className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
                <Button type="button" onClick={doImport} disabled={importing || extracting} data-testid="import-confirm" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileUp className="h-4 w-4 mr-1" />} Importa
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {isOwner && (
        <Dialog open={!!folderEdit} onOpenChange={(o) => !o && setFolderEdit(null)}>
          <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="folder-edit-dialog">
            <DialogHeader><DialogTitle className="font-head">Impostazioni cartella</DialogTitle></DialogHeader>
            {folderEdit && (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Nome cartella</Label>
                  <Input value={folderEdit.name} onChange={(e) => setFolderEdit({ ...folderEdit, name: e.target.value })} data-testid="folder-edit-name" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300 mb-2 block">Visibilità per tipologia di sinistro</Label>
                  <div className="flex flex-col gap-2">
                    <button type="button" onClick={() => setFolderEdit({ ...folderEdit, tipo_mode: "tutte" })} data-testid="folder-mode-tutte"
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${folderEdit.tipo_mode === "tutte" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 text-slate-300 hover:bg-slate-800/50"}`}>
                      <span className={`h-3.5 w-3.5 rounded-full border ${folderEdit.tipo_mode === "tutte" ? "border-amber-400 bg-amber-400" : "border-slate-500"}`} />
                      Tutte le tipologie
                    </button>
                    <button type="button" onClick={() => setFolderEdit({ ...folderEdit, tipo_mode: "specifiche" })} data-testid="folder-mode-specifiche"
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${folderEdit.tipo_mode === "specifiche" ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 text-slate-300 hover:bg-slate-800/50"}`}>
                      <span className={`h-3.5 w-3.5 rounded-full border ${folderEdit.tipo_mode === "specifiche" ? "border-amber-400 bg-amber-400" : "border-slate-500"}`} />
                      Tipologie specifiche
                    </button>
                  </div>
                </div>
                {folderEdit.tipo_mode === "specifiche" && (
                  <div className="flex flex-wrap gap-1.5" data-testid="folder-tipi">
                    {TIPI_SINISTRO.map((t) => (
                      <button key={t} type="button" onClick={() => toggleTipo(t)} data-testid={`folder-tipo-${t}`}
                        className={`text-xs font-semibold rounded-md border px-2.5 py-1.5 transition-colors ${folderEdit.tipi.includes(t) ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-slate-700 text-slate-300 hover:bg-slate-800/50"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-mono">Colore di sfondo</Label>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="folder-colors">
                    <button type="button" onClick={() => setFolderEdit({ ...folderEdit, colore: "" })} data-testid="folder-color-none" title="Nessun colore"
                      className={`h-7 px-2 rounded-md border text-[11px] ${!folderEdit.colore ? "border-amber-500 text-amber-500" : "border-slate-600 text-slate-400"}`}>Nessuno</button>
                    {FOLDER_COLORS.map((c) => (
                      <button key={c} type="button" onClick={() => setFolderEdit({ ...folderEdit, colore: c })} data-testid={`folder-color-${c.slice(1)}`} title={c}
                        style={{ backgroundColor: c }} className={`h-7 w-7 rounded-md border-2 transition-transform hover:scale-110 ${folderEdit.colore === c ? "border-slate-900 ring-2 ring-amber-400" : "border-transparent"}`} />
                    ))}
                    <input type="color" value={folderEdit.colore || "#f59e0b"} onChange={(e) => setFolderEdit({ ...folderEdit, colore: e.target.value })} data-testid="folder-color-custom" title="Colore personalizzato" className="h-7 w-9 cursor-pointer rounded border border-slate-600 bg-transparent" />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" onClick={saveFolder} disabled={savingFolder} data-testid="folder-edit-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                    {savingFolder ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva cartella
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
